'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import { usePathname } from 'next/navigation';
import { useNavbar } from '@/components/providers/NavbarProvider';

interface HeaderProps {
    className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
    const { isVisible } = useNavbar();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [showMobileNav, setShowMobileNav] = useState(false);
    const pathname = usePathname();

    // Handle scroll effect for glass morphism intensity + Mobile Nav Visibility
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
            // Show mobile nav buttons only after scrolling past Hero (approx 700px)
            setShowMobileNav(window.scrollY > 700);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navItems = [
        { label: 'Home', href: '/homepage' },
        { label: 'Services', href: '/services' },
        { label: 'Brand Lab', href: '/brand-visibility-lab' },
        { label: 'About', href: '/about' },
        { label: 'Contact', href: '/contact' },
    ];

    const getNextPage = (path: string) => {
        if (path === '/homepage' || path === '/') return { href: '/services', label: 'Services' };
        if (path === '/services') return { href: '/about', label: 'About' };
        if (path === '/about') return { href: '/brand-visibility-lab', label: 'Brand Lab' };
        if (path === '/brand-visibility-lab') return { href: '/contact', label: 'Contact' };
        return { href: '/services', label: 'Services' };
    };

    const getPrevPage = (path: string) => {
        if (path === '/homepage' || path === '/') return null; // No back from home
        if (path === '/services') return { href: '/homepage', label: 'Home' };
        if (path === '/about') return { href: '/services', label: 'Services' };
        if (path === '/brand-visibility-lab') return { href: '/about', label: 'About' };
        if (path === '/contact') return { href: '/brand-visibility-lab', label: 'Brand Lab' };
        return { href: '/homepage', label: 'Home' };
    };

    const nextInfo = getNextPage(pathname);
    const prevInfo = getPrevPage(pathname);

    return (
        <>
            <header
                className={`hidden lg:flex fixed top-4 left-0 right-0 z-50 justify-center px-4 transition-all duration-500 transform ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'
                    } ${className}`}
            >
                {/* ... content */}
                <div
                    className={`
                        w-full max-w-6xl transition-all duration-500 ease-in-out
                        ${scrolled || isMobileMenuOpen
                            ? 'bg-white/90 backdrop-blur-xl border-indigo-100/50 shadow-lg shadow-indigo-100/10'
                            : 'bg-transparent border-transparent shadow-none'
                        }
                        border rounded-2xl sm:rounded-full px-4 sm:px-6 py-3
                    `}
                >
                    <div className="flex items-center justify-between">
                        {/* Logo */}
                        <Link
                            href="/homepage"
                            className="relative flex items-center shrink-0"
                            aria-label="Site Map Genius Home"
                        >
                            <div
                                className={`
                                    relative overflow-hidden transition-all duration-500 ease-in-out
                                    ${scrolled ? 'h-10 w-36 sm:w-40' : 'h-24 w-48 lg:h-40 lg:w-80'}
                                `}
                            >
                                {/* Scrolled Logo (Brand Logo) */}
                                <div className={`absolute inset-0 transition-opacity duration-500 ${scrolled ? 'opacity-100' : 'opacity-0'}`}>
                                    <Image
                                        src="/navbarbrandlogo.jpeg"
                                        alt="Site Map Genius"
                                        fill
                                        className="object-contain object-left"
                                        priority
                                    />
                                </div>

                                {/* Hero Logo (Home Logo) */}
                                <div className={`absolute inset-0 transition-opacity duration-500 ${scrolled ? 'opacity-0' : 'opacity-100'}`}>
                                    <Image
                                        src="/Logo_Home_Herosection.png"
                                        alt="Site Map Genius"
                                        fill
                                        className="object-contain object-left"
                                        priority
                                    />
                                </div>
                            </div>
                        </Link>

                        {/* Desktop Nav */}
                        <nav className="hidden lg:flex items-center space-x-1">
                            {navItems.map((item) => {
                                // Enhanced Active Logic
                                const isActive =
                                    (item.href === '/homepage' && (pathname === '/' || pathname === '/homepage')) ||
                                    (item.href !== '/homepage' && pathname?.startsWith(item.href));

                                return (
                                    <Link
                                        key={item.href}
                                        href={item.href}
                                        className={`
                                            px-4 py-2 text-sm font-medium rounded-full transition-all duration-200
                                            ${isActive
                                                ? 'text-indigo-600 bg-indigo-50'
                                                : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-50'
                                            }
                                        `}
                                    >
                                        {item.label}
                                    </Link>
                                );
                            })}
                        </nav>

                        {/* Actions */}
                        <div className="flex items-center space-x-2 sm:space-x-3">
                            {/* Smart Nav (Mobile Only) - DISABLED in favor of Bottom Nav */}
                            {/*
                            <div className={`lg:hidden flex items-center space-x-1 transition-all duration-500 transform ${showMobileNav ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[-10px] pointer-events-none absolute'}`}>
                                {/* Prev Button }
                                {prevInfo && (
                                    <Link
                                        href={prevInfo.href}
                                        className="inline-flex items-center justify-center w-8 h-8 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full border border-indigo-100/50"
                                        aria-label={`Go back to ${prevInfo.label}`}
                                    >
                                        <Icon name="ArrowLeftIcon" className="w-3 h-3" />
                                    </Link>
                                )}

                                {/* Next Button }
                                {nextInfo && (
                                    <Link
                                        href={nextInfo.href}
                                        className="inline-flex items-center px-3 py-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full border border-indigo-100/50"
                                    >
                                        <span className="mr-1">{nextInfo.label}</span>
                                        <Icon name="ArrowRightIcon" className="w-3 h-3" />
                                    </Link>
                                )}
                            </div>

                            <Link
                                href="/contact"
                                className="hidden md:inline-flex items-center px-6 py-2.5 text-sm font-bold text-white bg-slate-900 hover:bg-gradient-to-r hover:from-indigo-600 hover:to-blue-600 rounded-full transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-indigo-500/25"
                            >
                                Let's Talk
                                <Icon name="ArrowRightIcon" className="ml-2 w-4 h-4" />
                            </Link>

                            {/* Mobile Menu Toggle */}
                            {/* Mobile Menu Toggle - REMOVED (Replaced by MobileNavbar) */}
                            {/* The hamburger button occupied 172-181, removing it forcefully now. */}
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Menu Dropdown - REMOVED */}
        </>
    );
};

export default Header;